import { FormControl } from 'bold-ui'
import { usePecField } from 'components/form'
import { confirm } from 'components/modals/confirm'
import { useFlags } from 'config/useFlagsContext'
import { css } from 'emotion'
import React, { useCallback } from 'react'
import { MetaRoot } from 'util/metaPath'
import {
  CondutaGroupEnum,
  CuidadoCompartilhadoModel,
  CuidadoCompartilhadoVideochamadaState,
  DiscussaoCasoFormModel,
  ResponsavelCuidadoCompartilhado,
} from 'view/cuidados-compartilhados/model-cuidadoCompartilhado'
import { isDiscussaoCasoCondutaValueDirty } from 'view/cuidados-compartilhados/util-cuidadoCompartilhado'

import { DiscussaoFormCustomLabelForConduta } from './DiscussaoCasoCustomLabelForConduta'
import { DiscussaoCasoExecutanteFields } from './DiscussaoCasoExecutanteFields'
import { DiscussaoCasoSolicitanteFields } from './DiscussaoCasoSolicitanteFields'

interface DiscussaoCasoFieldGroupProps {
  name: MetaRoot<DiscussaoCasoFormModel>
  cuidadoCompartilhado: CuidadoCompartilhadoModel
  videochamadaUuidState: CuidadoCompartilhadoVideochamadaState
  isEspecializada: boolean
  isExecutanteFromEstabelecimentoEspecializado: boolean
  responsavelCuidado: ResponsavelCuidadoCompartilhado
}

export type OnCondutaGroupChangeCallback = (newValue: CondutaGroupEnum, prevValue: CondutaGroupEnum) => Promise<boolean>

export const DiscussaoCasoFieldGroup = (props: Readonly<DiscussaoCasoFieldGroupProps>) => {
  const {
    name,
    cuidadoCompartilhado,
    videochamadaUuidState,
    isEspecializada,
    isExecutanteFromEstabelecimentoEspecializado,
    responsavelCuidado,
  } = props

  const { PILOTO_VINCULACAO_CUIDADO_ENABLED } = useFlags()

  const pathByResponsavel = responsavelCuidado === 'EXECUTANTE' ? name.executanteFields : name.solicitanteFields

  const {
    input: { value: currentConduta },
  } = usePecField({ name: pathByResponsavel.condutaGroup, subscription: { value: true } })

  const {
    input: { value: currentCondutaFieldValue },
  } = usePecField({ name: pathByResponsavel[currentConduta], subscription: { value: true } })

  const handleCondutaGroupChange: OnCondutaGroupChangeCallback = useCallback(
    async (newValue: CondutaGroupEnum, prevValue: CondutaGroupEnum): Promise<boolean> => {
      let changeAllowed = false
      if (prevValue !== newValue) {
        if (isDiscussaoCasoCondutaValueDirty(currentCondutaFieldValue, currentConduta, cuidadoCompartilhado.cidadao)) {
          await confirm({
            title: 'As informações adicionadas serão perdidas ao trocar de conduta.',
            body: 'Só é possível registrar uma conduta por resposta para a discussão.',
            confirmLabel: 'Continuar aqui',
            cancelLabel: 'Trocar de conduta',
            icon: 'infoCircleOutline',
            onCancel: () => {
              changeAllowed = true
            },
          })()
        } else {
          changeAllowed = true
        }
      }
      return changeAllowed
    },
    [cuidadoCompartilhado.cidadao, currentConduta, currentCondutaFieldValue]
  )

  return (
    <FormControl
      label={PILOTO_VINCULACAO_CUIDADO_ENABLED ? <DiscussaoFormCustomLabelForConduta /> : 'Conduta'}
      style={styles.condutas}
      required={!PILOTO_VINCULACAO_CUIDADO_ENABLED}
    >
      {responsavelCuidado === 'EXECUTANTE' && (
        <DiscussaoCasoExecutanteFields
          path={name.executanteFields}
          cuidadoCompartilhado={cuidadoCompartilhado}
          videochamadaUuidState={videochamadaUuidState}
          isEspecializada={isEspecializada}
          isExecutanteFromEstabelecimentoEspecializado={isExecutanteFromEstabelecimentoEspecializado}
          onCondutaGroupChange={handleCondutaGroupChange}
        />
      )}
      {responsavelCuidado === 'SOLICITANTE' && (
        <DiscussaoCasoSolicitanteFields
          path={name.solicitanteFields}
          cuidadoCompartilhado={cuidadoCompartilhado}
          videochamadaUuidState={videochamadaUuidState}
          isEspecializada={isEspecializada}
          isExecutanteFromEstabelecimentoEspecializado={isExecutanteFromEstabelecimentoEspecializado}
          onCondutaGroupChange={handleCondutaGroupChange}
        />
      )}
    </FormControl>
  )
}

const styles = {
  condutas: css`
    margin: 0 1rem;
  `,
}
